
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useRef } from 'react';

export interface TeacherMatchProfile {
  id: string;
  name: string;
  code: string;
  nickname: string;
  email: string;
  tier: string;
  gender: string;
  personality: string;
  university: string;
  major: string;
  trial_lesson: string;
  teaching_class: string;
  status: string;
  subjects: Array<{ subject: string; education_stage: string; course_type: string }>;
  specialized: string[];
  teaching_styles: Array<{ teaching_style: string; level: number }>;
  branches: string[];
  certificates: Array<{ certificate_id: string; grade: string; status: string; note: string }>;
  student_proficiency: string[];
  has_line_link: boolean;
  taught_selected_student: boolean;
}

export interface TeacherMatchFilters {
  search: string;
  subjects: string[];
  educationStages: string[];
  courseTypes: string[];
  interSubTypes: string[];
  specialized: string[];
  teachingStyles: string[];
  styleLevels: number[];
  gender: string[];
  personality: string[];
  trialLesson: string[];
  branches: string[];
  studentProficiency: string[];
  status: string;
  university: string;
  major: string;
  selectedStudentCode: string;
  availabilitySlots: AvailabilitySlot[];
  taughtSelectedStudent: 'any' | 'yes' | 'no';
  studentMode: 'none' | 'system' | 'trial' | 'manual';
  selectedClassTrialId: string;
  selectedStudentDisplayName: string;
}

export interface AvailabilitySlot {
  day: number;        // 1=Mon..7=Sun
  start: string;      // 'HH:mm' or ''
  end: string;        // 'HH:mm' or ''
}

export const TEACHER_MATCH_PAGE_SIZE = 20;

const EMPTY_FILTERS: TeacherMatchFilters = {
  search: '',
  subjects: [],
  educationStages: [],
  courseTypes: [],
  interSubTypes: [],
  specialized: [],
  teachingStyles: [],
  styleLevels: [],
  gender: [],
  personality: [],
  trialLesson: [],
  branches: [],
  studentProficiency: [],
  status: 'active',
  university: '',
  major: '',
  selectedStudentCode: '',
  availabilitySlots: [],
  taughtSelectedStudent: 'any',
  studentMode: 'none',
  selectedClassTrialId: '',
  selectedStudentDisplayName: '',
};

// Parse URLSearchParams → TeacherMatchFilters. Module-level helper so both
// the read path (useMemo) and the write path (functional setSearchParams) can
// share the same logic and stay in sync.
const parseFiltersFromParams = (sp: URLSearchParams): TeacherMatchFilters => ({
  search: sp.get('q') || '',
  subjects: sp.get('subjects')?.split(',').filter(Boolean) || [],
  educationStages: sp.get('stages')?.split(',').filter(Boolean) || [],
  courseTypes: sp.get('courseTypes')?.split(',').filter(Boolean) || [],
  interSubTypes: sp.get('interSub')?.split(',').filter(Boolean) || [],
  specialized: sp.get('specialized')?.split(',').filter(Boolean) || [],
  teachingStyles: sp.get('styles')?.split(',').filter(Boolean) || [],
  styleLevels: sp.get('styleLevels')?.split(',').filter(Boolean).map(Number) || [],
  gender: sp.get('gender')?.split(',').filter(Boolean) || [],
  personality: sp.get('personality')?.split(',').filter(Boolean) || [],
  trialLesson: sp.get('trial')?.split(',').filter(Boolean) || [],
  branches: sp.get('branches')?.split(',').filter(Boolean) || [],
  studentProficiency: sp.get('proficiency')?.split(',').filter(Boolean) || [],
  status: sp.get('status') || 'active',
  university: sp.get('university') || '',
  major: sp.get('major') || '',
  selectedStudentCode: sp.get('student') || '',
  availabilitySlots: (sp.get('slots') || '')
    .split(',')
    .filter(Boolean)
    .map(s => {
      const [d, st, en] = s.split('|');
      const day = parseInt(d, 10);
      if (isNaN(day) || day < 0 || day > 6) return null;
      return { day, start: st || '', end: en || '' };
    })
    .filter(Boolean) as AvailabilitySlot[],
  taughtSelectedStudent: ((): 'any' | 'yes' | 'no' => {
    const v = sp.get('taught');
    return v === 'yes' || v === 'no' ? v : 'any';
  })(),
  studentMode: ((): 'none' | 'system' | 'trial' | 'manual' => {
    const v = sp.get('studentMode');
    return v === 'system' || v === 'trial' || v === 'manual' ? v : 'none';
  })(),
  selectedClassTrialId: sp.get('classTrial') || '',
  selectedStudentDisplayName: sp.get('manualName') || '',
});

// Serialize TeacherMatchFilters → URLSearchParams. Mirror of parseFiltersFromParams.
const serializeFiltersToParams = (f: TeacherMatchFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (f.search) params.set('q', f.search);
  if (f.subjects.length) params.set('subjects', f.subjects.join(','));
  if (f.educationStages.length) params.set('stages', f.educationStages.join(','));
  if (f.courseTypes.length) params.set('courseTypes', f.courseTypes.join(','));
  if (f.interSubTypes.length) params.set('interSub', f.interSubTypes.join(','));
  if (f.specialized.length) params.set('specialized', f.specialized.join(','));
  if (f.teachingStyles.length) params.set('styles', f.teachingStyles.join(','));
  if (f.styleLevels.length) params.set('styleLevels', f.styleLevels.join(','));
  if (f.gender.length) params.set('gender', f.gender.join(','));
  if (f.personality.length) params.set('personality', f.personality.join(','));
  if (f.trialLesson.length) params.set('trial', f.trialLesson.join(','));
  if (f.branches.length) params.set('branches', f.branches.join(','));
  if (f.studentProficiency.length) params.set('proficiency', f.studentProficiency.join(','));
  if (f.status && f.status !== 'active') params.set('status', f.status);
  if (f.university) params.set('university', f.university);
  if (f.major) params.set('major', f.major);
  if (f.selectedStudentCode) params.set('student', f.selectedStudentCode);
  if (f.availabilitySlots.length) {
    params.set(
      'slots',
      f.availabilitySlots.map(s => `${s.day}|${s.start}|${s.end}`).join(','),
    );
  }
  if (f.taughtSelectedStudent && f.taughtSelectedStudent !== 'any') params.set('taught', f.taughtSelectedStudent);
  if (f.studentMode && f.studentMode !== 'none') params.set('studentMode', f.studentMode);
  if (f.selectedClassTrialId) params.set('classTrial', f.selectedClassTrialId);
  if (f.selectedStudentDisplayName) params.set('manualName', f.selectedStudentDisplayName);
  return params;
};

export const useTeacherMatch = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: TeacherMatchFilters = useMemo(
    () => parseFiltersFromParams(searchParams),
    [searchParams],
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  }, [searchParams]);

  // Mutable ref tracking the latest URL params. We need this because
  // react-router-dom's useSearchParams functional updater receives the
  // closure's `searchParams`, NOT React's batched state — so multiple
  // updateFilters() calls within the same render cycle all see the same
  // stale value and clobber each other (auto-fill race condition).
  //
  // We sync the ref to searchParams on every render and update it
  // synchronously inside updateFilters() so subsequent calls within the
  // same tick observe the merged result.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const updateFilters = useCallback((updates: Partial<TeacherMatchFilters>) => {
    const current = parseFiltersFromParams(searchParamsRef.current);
    const merged: TeacherMatchFilters = { ...current, ...updates };
    const nextParams = serializeFiltersToParams(merged);
    // Update the ref BEFORE setSearchParams so concurrent calls within the
    // same tick see the merged state.
    searchParamsRef.current = nextParams;
    setSearchParams(nextParams, { replace: true });
  }, [setSearchParams]);

  const setPage = useCallback((nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (nextPage <= 1) params.delete('page');
    else params.set('page', String(nextPage));
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' ||
      filters.subjects.length > 0 ||
      filters.educationStages.length > 0 ||
      filters.courseTypes.length > 0 ||
      filters.interSubTypes.length > 0 ||
      filters.specialized.length > 0 ||
      filters.teachingStyles.length > 0 ||
      filters.styleLevels.length > 0 ||
      filters.gender.length > 0 ||
      filters.personality.length > 0 ||
      filters.trialLesson.length > 0 ||
      filters.branches.length > 0 ||
      filters.studentProficiency.length > 0 ||
      filters.status !== 'active' ||
      filters.university !== '' ||
      filters.major !== '' ||
      filters.selectedStudentCode !== '' ||
      filters.availabilitySlots.length > 0 ||
      filters.taughtSelectedStudent !== 'any' ||
      filters.studentMode !== 'none' ||
      filters.selectedClassTrialId !== '' ||
      filters.selectedStudentDisplayName !== '';
  }, [filters]);

  // Server-side filtered + paginated teachers via RPC.
  // Filter logic lives in DB function `match_teachers` to bypass PostgREST 1000-row limit
  // and to scale beyond a few hundred teachers.
  const { data: pageData, isLoading, error } = useQuery({
    queryKey: ['teacher-match-rows', filters, page],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('match_teachers' as any, {
        p_filters: filters as any,
        p_limit: TEACHER_MATCH_PAGE_SIZE,
        p_offset: (page - 1) * TEACHER_MATCH_PAGE_SIZE,
      });
      if (error) throw error;
      const payload = (data || {}) as { total?: number; rows?: TeacherMatchProfile[] };
      return {
        rows: (payload.rows || []) as TeacherMatchProfile[],
        total: payload.total || 0,
      };
    },
    staleTime: 60 * 1000,
  });

  // Distinct filter options (cached longer; rarely changes)
  const { data: filterOptionsData } = useQuery({
    queryKey: ['teacher-match-options'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('match_teachers_filter_options' as any);
      if (error) throw error;
      return data as {
        subjects: string[];
        educationStages: string[];
        courseTypes: string[];
        specialized: string[];
        teachingStyles: string[];
        styleLevels: number[];
        gender: string[];
        personality: string[];
        trialLesson: string[];
        branches: string[];
        studentProficiency: string[];
      };
    },
    staleTime: 10 * 60 * 1000,
  });

  const teachers = pageData?.rows || [];
  const totalCount = pageData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / TEACHER_MATCH_PAGE_SIZE));

  // Compute matched attributes per teacher for highlighting
  const getMatchedAttributes = useCallback((teacher: TeacherMatchProfile) => {
    const matched: string[] = [];
    if (filters.subjects.length) {
      teacher.subjects.filter(s => filters.subjects.includes(s.subject)).forEach(s => matched.push(s.subject));
    }
    if (filters.educationStages.length) {
      teacher.subjects.filter(s => filters.educationStages.includes(s.education_stage)).forEach(s => matched.push(s.education_stage));
    }
    if (filters.specialized.length) {
      teacher.specialized.filter(s => filters.specialized.includes(s)).forEach(s => matched.push(s));
    }
    if (filters.teachingStyles.length) {
      teacher.teaching_styles.filter(s => filters.teachingStyles.includes(s.teaching_style)).forEach(s => matched.push(s.teaching_style));
    }
    if (filters.branches.length) {
      teacher.branches.filter(b => filters.branches.includes(b)).forEach(b => matched.push(b));
    }
    return matched;
  }, [filters]);

  return {
    teachers,
    totalCount,
    page,
    pageSize: TEACHER_MATCH_PAGE_SIZE,
    totalPages,
    setPage,
    isLoading,
    error,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    filterOptions: filterOptionsData || null,
    getMatchedAttributes,
  };
};

